<template>
  <v-card class="h-100 d-flex flex-column">
    <v-card-title>Utopia Dashboard</v-card-title>
    <v-row class="px-4">
      <v-col cols="9">
        <v-select
          v-model="selectedHeaders"
          :items="allHeaders"
          label="Select Columns"
          multiple
          chips
          deletable-chips
        ></v-select>
      </v-col>
      <v-col cols="3">
        <v-btn color="primary" @click="exportToCsv">Export to CSV</v-btn>
      </v-col>
    </v-row>
    <v-data-table
      :headers="visibleHeaders"
      :items="filteredData"
      :loading="loading"
      sort-by="tenant_name"
      class="elevation-1 flex-grow-1"
    >
      <template v-for="header in visibleHeaders" v-slot:[`header.${header.value}`]>
        <div :key="header.value" class="pa-1">
          <div>{{ header.text }}</div>
          <div v-if="['centres', 'members'].includes(header.value)">
            <v-row dense>
              <v-col cols="5">
                <v-select
                  :items="numericFilterOperators"
                  v-model="filters[header.value].operator"
                  dense
                  hide-details
                  class="pa-0 ma-0"
                ></v-select>
              </v-col>
              <v-col cols="7">
                <v-text-field
                  v-model.number="filters[header.value].value"
                  type="number"
                  dense
                  hide-details
                  class="pa-0 ma-0"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>
          <v-autocomplete
            v-else-if="['tenant_name', 'bridge', 'gateway'].includes(header.value)"
            v-model="filters[header.value]"
            :items="columnValues[header.value]"
            multiple
            chips
            deletable-chips
            single-line
            hide-details
            class="mt-1"
            clearable
          ></v-autocomplete>
          <v-select
            v-else
            v-model="filters[header.value]"
            :items="columnValues[header.value]"
            multiple
            single-line
            hide-details
            class="mt-1"
            clearable
          >
            <template v-slot:prepend-item>
              <v-list-item
                ripple
                @click="toggleSelectAll(header.value)"
              >
                <v-list-item-action>
                  <v-icon>{{ getSelectAllIcon(header.value) }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Select All</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
          </v-select>
        </div>
      </template>
    </v-data-table>
    <v-divider></v-divider>
    <v-card-title>Calculations</v-card-title>
    <v-container fluid>
      <v-row>
        <v-col cols="6">
          <v-list dense>
            <v-list-item v-for="header in calcColumnsLeft" :key="header.value">
              <v-list-item-content>
                <v-row>
                  <v-col cols="3" class="text-right">
                    <v-list-item-title>{{ header.text.replace('Has ', '') }}:</v-list-item-title>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      v-model.number="calcInputs[header.value]"
                      type="number"
                      dense
                      hide-details
                      class="pa-0 ma-0"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-select
                      v-model="calcTypes[header.value]"
                      :items="calcTypeOptions"
                      dense
                      hide-details
                      class="pa-0 ma-0"
                    ></v-select>
                  </v-col>
                  <v-col cols="3">
                    <v-list-item-subtitle class="text-left font-weight-bold">= {{ formatCurrency(calculations[header.value]) }}</v-list-item-subtitle>
                  </v-col>
                </v-row>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>
        <v-col cols="6">
          <v-list dense>
            <v-list-item v-for="header in calcColumnsRight" :key="header.value">
              <v-list-item-content>
                <v-row>
                  <v-col cols="3" class="text-right">
                    <v-list-item-title>{{ header.text.replace('Has ', '') }}:</v-list-item-title>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      v-model.number="calcInputs[header.value]"
                      type="number"
                      dense
                      hide-details
                      class="pa-0 ma-0"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-select
                      v-model="calcTypes[header.value]"
                      :items="calcTypeOptions"
                      dense
                      hide-details
                      class="pa-0 ma-0"
                    ></v-select>
                  </v-col>
                  <v-col cols="3">
                    <v-list-item-subtitle class="text-left font-weight-bold">= {{ formatCurrency(calculations[header.value]) }}</v-list-item-subtitle>
                  </v-col>
                </v-row>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
      <v-divider></v-divider>
      <v-row>
        <v-col class="text-right font-weight-bold text-h6">
          Total: {{ formatCurrency(totalCalculation) }}
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import axios from 'axios';

export default {
  name: 'UtopiaDashboard',
  data() {
    const allHeaders = [
      { text: 'ID', value: 'id' },
      { text: 'Tenant Name', value: 'tenant_name' },
      { text: 'Centres', value: 'centres' },
      { text: 'Members', value: 'members' },
      { text: 'Status', value: 'status' },
      { text: 'Region', value: 'region_name' },
      { text: 'Bridge', value: 'bridge' },
      { text: 'Has CoursePro', value: 'has_coursepro' },
      { text: 'Has SMS', value: 'has_sms' },
      { text: 'Gateway', value: 'gateway' },
      { text: 'Has SDD', value: 'has_sdd' },
      { text: 'Has Orgs', value: 'has_orgs' },
      { text: 'Has Ads', value: 'has_ads' },
      { text: 'Has DataHub', value: 'has_datahub' },
      { text: 'Has HomePortal', value: 'has_homeportal' },
      { text: 'Has GTM', value: 'has_gtm' },
      { text: 'Has Vouchers', value: 'has_vouchers' },
      { text: 'Has Strive', value: 'has_strive' },
      { text: 'Has CommsPortal', value: 'has_commsportal' },
      { text: 'Has Insight', value: 'has_insight' },
      { text: 'Created', value: 'created' },
      { text: 'Created By', value: 'created_by' },
    ];

    const hasColumns = allHeaders.filter(h => h.value.startsWith('has_'));

    const defaultCalcValues = {
      has_coursepro: 1000,
      has_sms: 0.06,
      has_sdd: 0.30,
      has_orgs: 250,
      has_ads: 100,
      has_datahub: 300,
      has_homeportal: 60,
      has_gtm: 300,
      has_vouchers: 100,
      has_strive: 0,
      has_commsportal: 20,
      has_insight: 100,
    };

    const defaultCalcTypes = {
      has_sms: 'per_user_per_month',
      has_sdd: 'per_user_per_month',
      has_ads: 'per_tenant',
      has_datahub: 'per_tenant',
      has_gtm: 'per_tenant',
      has_vouchers: 'per_tenant',
    };

    const calcInputs = {};
    const calcTypes = {};
    hasColumns.forEach(h => {
      calcInputs[h.value] = defaultCalcValues[h.value] || 30;
      calcTypes[h.value] = defaultCalcTypes[h.value] || 'per_site';
    });

    return {
      loading: false,
      allHeaders,
      selectedHeaders: ['tenant_name', 'centres', 'bridge', 'has_coursepro', 'has_sms', 'gateway', 'has_sdd', 'has_orgs', 'has_ads', 'has_datahub', 'has_homeportal', 'has_gtm', 'has_vouchers', 'has_strive', 'has_commsportal', 'has_insight'],
      utopiaData: [],
      filters: {
        centres: { operator: null, value: null },
        members: { operator: null, value: null },
      },
      calcInputs,
      calcTypes,
      calcTypeOptions: [
        { text: 'Per Site', value: 'per_site' },
        { text: 'Per Tenant', value: 'per_tenant' },
        { text: 'Per Site Per Month', value: 'per_site_per_month' },
        { text: 'Per User Per Month', value: 'per_user_per_month' },
      ],
      numericFilterOperators: ['=', '<', '>', '<=', '>='],
    };
  },
  computed: {
    visibleHeaders() {
      return this.allHeaders.filter(h => this.selectedHeaders.includes(h.value));
    },
    hasColumnsForCalc() {
      const visibleHasColumns = this.visibleHeaders.filter(h => h.value.startsWith('has_'));

      return visibleHasColumns.filter(h => {
        if (!this.filteredData || this.filteredData.length === 0) {
          return false;
        }
        const allYes = this.filteredData.every(item => item[h.value] === 'yes');
        return !allYes;
      });
    },
    calcColumnsLeft() {
      const half = Math.ceil(this.hasColumnsForCalc.length / 2);
      return this.hasColumnsForCalc.slice(0, half);
    },
    calcColumnsRight() {
      const half = Math.ceil(this.hasColumnsForCalc.length / 2);
      return this.hasColumnsForCalc.slice(half);
    },
    processedData() {
      if (!this.utopiaData) return [];
      return this.utopiaData.map(item => {
        let region = item.region_name;
        if (typeof region === 'string') {
          if (region.startsWith('eu')) {
            region = 'EU';
          } else if (region.startsWith('us')) {
            region = 'US';
          }
        }
        return {
          ...item,
          region_name: region,
          has_coursepro: item.coursepro_env ? 'yes' : 'no',
          has_homeportal: item.homeportal_env ? 'yes' : 'no',
          has_strive: item.strive_env ? 'yes' : 'no',
          has_commsportal: item.commsportal_env ? 'yes' : 'no',
          has_insight: item.insight_env ? 'yes' : 'no',
        };
      });
    },
    columnValues() {
      const values = {};
      if (!this.processedData) return values;

      this.allHeaders.forEach(header => {
        const key = header.value;
        const uniqueValues = new Set();
        this.processedData.forEach(item => {
          if (item[key] !== null && item[key] !== undefined) {
            uniqueValues.add(item[key]);
          }
        });
        values[key] = Array.from(uniqueValues).sort();
      });
      return values;
    },
    filteredData() {
      if (!this.processedData) return [];
      let data = this.processedData;

      return data.filter(item => {
        return Object.keys(this.filters).every(key => {
          const filterValue = this.filters[key];

          if (filterValue === null || filterValue === '' || filterValue === undefined) {
            return true; // No filter
          }

          const itemValue = item[key];
          if (itemValue === null || itemValue === undefined) {
            return false;
          }

          if (Array.isArray(filterValue)) {
            // Multi-select filter
            if (filterValue.length === 0) return true;
            return filterValue.includes(itemValue);
          } else if (typeof filterValue === 'object' && filterValue !== null) {
            // Custom filter with operator and value
            const { operator, value } = filterValue;
            if (!operator || value === null || value === '') {
              return true; // No filter
            }
            const numericItemValue = parseInt(itemValue, 10);
            const numericFilterValue = parseInt(value, 10);
            if (isNaN(numericItemValue) || isNaN(numericFilterValue)) {
              return false;
            }
            switch (operator) {
              case '=': return numericItemValue === numericFilterValue;
              case '<': return numericItemValue < numericFilterValue;
              case '>': return numericItemValue > numericFilterValue;
              case '<=': return numericItemValue <= numericFilterValue;
              case '>=': return numericItemValue >= numericFilterValue;
              default: return true;
            }
          } else {
            // Text filter
            return String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
          }
        });
      });
    },
    calculations() {
      const results = {};
      if (!this.filteredData) return results;

      this.hasColumnsForCalc.forEach(header => {
        const key = header.value;
        const inputValue = this.calcInputs[key] || 0;
        const calcType = this.calcTypes[key];

        const sum = this.filteredData
          .filter(item => item[key] === 'no')
          .reduce((total, item) => {
            const centres = parseInt(item.centres, 10);
            const members = parseInt(item.members, 10);
            let value = 0;

            switch (calcType) {
              case 'per_site':
                if (!isNaN(centres)) {
                  value = centres * inputValue;
                }
                break;
              case 'per_tenant':
                value = inputValue;
                break;
              case 'per_site_per_month':
                if (!isNaN(centres)) {
                  value = centres * inputValue * 12;
                }
                break;
              case 'per_user_per_month':
                if (!isNaN(members)) {
                  value = members * inputValue * 12;
                }
                break;
            }
            return total + value;
          }, 0);

        results[key] = sum;
      });

      return results;
    },
    totalCalculation() {
      if (!this.calculations) return 0;
      return Object.values(this.calculations).reduce((sum, value) => sum + value, 0);
    },
  },
  created() {
    this.fetchUtopiaData();
  },
  methods: {
    fetchUtopiaData() {
      this.loading = true;
      axios.get('/api/utopia')
        .then(response => {
          this.utopiaData = response.data.data;
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching Utopia data:', error);
          this.loading = false;
        });
    },
    toggleSelectAll(key) {
      if (this.isAllSelected(key)) {
        this.$set(this.filters, key, []);
      } else {
        this.$set(this.filters, key, this.columnValues[key].slice());
      }
    },
    isAllSelected(key) {
      if (!this.filters[key] || !this.columnValues[key]) return false;
      return this.filters[key].length === this.columnValues[key].length;
    },
    isIndeterminate(key) {
        if (!this.filters[key] || !this.columnValues[key]) return false;
        return this.filters[key].length > 0 && !this.isAllSelected(key);
    },
    getSelectAllIcon(key) {
        if (this.isAllSelected(key)) return 'mdi-close-box';
        if (this.isIndeterminate(key)) return 'mdi-minus-box';
        return 'mdi-checkbox-blank-outline';
    },
    formatCurrency(value) {
      if (typeof value !== 'number') {
        return value;
      }
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(value);
    },
    calculateRowTotal(item) {
      let total = 0;
      this.hasColumnsForCalc.forEach(header => {
        const key = header.value;
        if (item[key] === 'no') {
          const inputValue = this.calcInputs[key] || 0;
          const calcType = this.calcTypes[key];
          const centres = parseInt(item.centres, 10);
          const members = parseInt(item.members, 10);
          let value = 0;

          switch (calcType) {
            case 'per_site':
              if (!isNaN(centres)) {
                value = centres * inputValue;
              }
              break;
            case 'per_tenant':
              value = inputValue;
              break;
            case 'per_site_per_month':
              if (!isNaN(centres)) {
                value = centres * inputValue * 12;
              }
              break;
            case 'per_user_per_month':
              if (!isNaN(members)) {
                value = members * inputValue * 12;
              }
              break;
          }
          total += value;
        }
      });
      return total;
    },
    generateFilename() {
      const date = new Date().toISOString().slice(0, 10);
      let filename = `utopia-${date}`;

      for (const key in this.filters) {
        const filterValue = this.filters[key];
        if (Array.isArray(filterValue)) {
          if (filterValue.length > 0 && this.columnValues[key] && filterValue.length < this.columnValues[key].length) {
            const filterText = filterValue.map(v => String(v).toLowerCase()).join('_');
            let column = key.replace('has_', '');
            if (filterValue.length === 1 && filterValue[0] === 'no') {
              filename += `-no-${column}`;
            } else if (filterValue.length === 1 && filterValue[0] === 'yes') {
              filename += `-with-${column}`;
            } else {
              filename += `-${column}-${filterText}`;
            }
          }
        } else if (typeof filterValue === 'object' && filterValue !== null) {
          const { operator, value } = filterValue;
          if (operator && value) {
            let column = key.replace('has_', '');
            filename += `-${column}${operator}${value}`;
          }
        } else if (typeof filterValue === 'string' && filterValue) {
          let column = key.replace('has_', '');
          filename += `-${column}-${filterValue.toLowerCase()}`;
        }
      }

      return filename + '.csv';
    },
    exportToCsv() {
      const headers = [...this.visibleHeaders.map(h => h.text), 'Total Calculated Value'];
      const rows = this.filteredData.map(item => [
        ...this.visibleHeaders.map(header => {
          let value = item[header.value];
          if (value === null || value === undefined) {
            value = '';
          }
          let stringValue = String(value);
          if (stringValue.includes(',')) {
            stringValue = `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }),
        this.calculateRowTotal(item)
      ]);

      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', this.generateFilename());
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
</style>